import { createContext, useContext, useEffect, useMemo, useReducer } from "react";

const GlobalFiltersContext = createContext(null);

const STORAGE_KEY = "globalFilters";

const INITIAL_STATE = {
    warehouseIds: []
};

function normalizeValue(value) {
    if (Array.isArray(value)) {
        return [...value].sort((a, b) => {
            if (typeof a === "number" && typeof b === "number") {
                return a - b;
            }
            return String(a).localeCompare(String(b));
        });
    }
    return value;
}

function normalizeFiltersObject(filters) {
    const normalized = {};
    Object.entries(filters).forEach(([key, value]) => {
        normalized[key] = normalizeValue(value);
    });
    return normalized;
}

function getInitialState() {
    try {
        const stored = sessionStorage.getItem(STORAGE_KEY);
        if (!stored) return INITIAL_STATE;

        const parsed = JSON.parse(stored);
        return normalizeFiltersObject({
            ...INITIAL_STATE,
            ...parsed
        });
    } catch {
        return INITIAL_STATE;
    }
}

function reducer(state, action) {
    switch (action.type) {
        case "SET_FILTER": {
            const nextState = normalizeFiltersObject({
                ...state,
                [action.key]: action.payload
            });

            if (JSON.stringify(nextState) === JSON.stringify(state)) return state;

            return nextState;
        }
        case "RESET_FILTER": {
            if (!state[action.key]?.length) return state;

            return {
                ...state,
                [action.key]: []
            };
        }
        case "RESET_GLOBAL_FILTERS":
            return normalizeFiltersObject(INITIAL_STATE);

        default:
            return state;
    }
}

export function GlobalFiltersProvider({ children }) {
    const [state, dispatch] = useReducer(reducer, undefined, getInitialState);

    useEffect(() => {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    }, [state]);

    const value = useMemo(() => ({
        filters: state,

        setFilter: (key, value) =>
            dispatch({ type: "SET_FILTER", key, payload: value }),

        resetFilter: (key) =>
            dispatch({ type: "RESET_FILTER", key }),

        resetGlobalFilters: () =>
            dispatch({ type: "RESET_GLOBAL_FILTERS" }),

    }), [state]);

    return (
        <GlobalFiltersContext.Provider value={value}>
            {children}
        </GlobalFiltersContext.Provider>
    );
}

export function useGlobalFilters() {
    const ctx = useContext(GlobalFiltersContext);
    if (!ctx) {
        throw new Error("useGlobalFilters must be used inside GlobalFiltersProvider");
    }
    return ctx;
}